import { MaterialIcon } from '@components/common/MaterialIcon';
import { DROPDOWN_TRANSITION_PROPS } from '@constants/common';
import {
  ARTICLE_ARCHITECT_REVIEW_ROUTE,
  TRAIN_AI_PAGE_ROUTE,
} from '@constants/routes';
import { MenuItem, MenuItems, Transition } from '@headlessui/react';
import { useHasTrainAIAccess } from '@hooks/useHasTrainAIAccess';
import clsx from 'clsx';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

interface NotificationCenterDropdownProps {
  trainAiCount: number | null;
  knowledgeArticleCount: number | null;
}

interface NotificationRowProps {
  iconName: string;
  iconBgClass: string;
  iconColorClass: string;
  description: string;
  onClick: () => void;
}

const NotificationRow: React.FC<NotificationRowProps> = ({
  iconName,
  iconBgClass,
  iconColorClass,
  description,
  onClick,
}) => {
  return (
    <MenuItem>
      {({ focus }) => (
        <div
          className={clsx(
            'flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-gray-100 last:border-b-0',
            focus && 'bg-primary-hover',
          )}
          onClick={onClick}>
          <div className='w-1.5 h-1.5 rounded-full bg-primary-base flex-shrink-0' />
          <div
            className={clsx(
              'w-[30px] h-[30px] rounded-lg flex items-center justify-center flex-shrink-0',
              iconBgClass,
            )}>
            <MaterialIcon
              icon={iconName}
              type='outlined'
              classes={['text-base', 'flex-shrink-0', iconColorClass]}
            />
          </div>
          <span className='text-tiny flex-1 text-gray-600'>{description}</span>
          <MaterialIcon
            icon='chevron_right'
            classes={['text-gray-400', 'text-base', 'flex-shrink-0']}
          />
        </div>
      )}
    </MenuItem>
  );
};

export const NotificationCenterDropdown: React.FC<
  NotificationCenterDropdownProps
> = ({ trainAiCount, knowledgeArticleCount }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // * The row sends the user to the Train A.I. page, so a studio without that page
  // * must not show it - the click would land on the default page instead.
  const isTrainAiStudio = useHasTrainAIAccess();
  const hasTrainAiRow =
    isTrainAiStudio && trainAiCount !== null && trainAiCount > 0;
  const hasKnowledgeArticleRow =
    knowledgeArticleCount !== null && knowledgeArticleCount > 0;
  const hasRows = hasTrainAiRow || hasKnowledgeArticleRow;

  return (
    <Transition {...DROPDOWN_TRANSITION_PROPS}>
      <MenuItems className='absolute top-full mt-2 right-0 w-[320px] bg-white border border-gray-200 rounded-xl shadow-around z-[12] focus:outline-none focus-visible:outline-none overflow-hidden'>
        <div className='px-4 py-3.5 border-b border-gray-200 flex items-center justify-between bg-slate-50'>
          <span className='text-xs font-semibold text-gray-900'>
            {t('notification-center.title')}
          </span>
        </div>
        {!hasRows && (
          <div className='px-4 py-6 text-center text-tiny italic text-gray-400 font-normal'>
            {t('notification-center.empty')}
          </div>
        )}
        {hasTrainAiRow && (
          <NotificationRow
            iconName='local_fire_department'
            iconBgClass='bg-primary-lightest'
            iconColorClass='text-primary-base'
            description={t('notification-center.train-ai.description', {
              count: trainAiCount,
            })}
            onClick={() => navigate(TRAIN_AI_PAGE_ROUTE)}
          />
        )}
        {hasKnowledgeArticleRow && (
          <NotificationRow
            iconName='description'
            iconBgClass='bg-gray-100'
            iconColorClass='text-gray-500'
            description={t(
              'notification-center.article-architect.description',
              {
                count: knowledgeArticleCount,
              },
            )}
            onClick={() => navigate(ARTICLE_ARCHITECT_REVIEW_ROUTE)}
          />
        )}
      </MenuItems>
    </Transition>
  );
};
